import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

let vectorMapPromise = null

function loadVectorMap() {
  vectorMapPromise ||= import("jsvectormap").then(async (mod) => {
    await import("jsvectormap/dist/maps/world.js")
    return mod.default
  })
  return vectorMapPromise
}

export default class extends Controller {
  static values = {
    regions: Object,
    stats: Object,
    urls: Object
  }

  // jsvectormap + the world map are ~140 KB and only the admin dashboard draws them, so
  // they load on first connect from their own chunk (esbuild code splitting).
  async connect() {
    this.jsVectorMap = await loadVectorMap()
    if (!this.element.isConnected) return
    this.destroyMap()
    this.renderMap()
  }

  disconnect() {
    this.destroyMap()
  }

  regionsValueChanged() {
    if (this.map) {
      this.destroyMap()
      this.renderMap()
    }
  }

  destroyMap() {
    if (this.map) {
      try { this.map.destroy() } catch { /* DOM already removed by Turbo morph */ }
      this.map = null
    }
    this.element.innerHTML = ""
  }

  renderMap() {
    const stats = this.statsValue
    const urls = this.urlsValue
    let isDragging = false

    this.element.addEventListener("mousedown", () => { isDragging = false })
    this.element.addEventListener("mousemove", () => { isDragging = true })

    this.map = new this.jsVectorMap({
      selector: this.element,
      map: "world",
      backgroundColor: "transparent",
      zoomButtons: true,
      zoomOnScroll: true,
      zoomOnScrollSpeed: 0.3,
      zoomStep: 1.1,
      showTooltip: true,
      regionStyle: {
        initial: {
          fill: "#F0F4F8",
          "fill-opacity": 1,
          stroke: "#CBD5E0",
          "stroke-width": 0.5,
          "stroke-opacity": 1
        },
        hover: {
          "fill-opacity": 0.8,
          cursor: "pointer"
        }
      },
      series: {
        regions: [{
          attribute: "fill",
          attributes: this.regionsValue
        }]
      },
      onRegionClick(event, code) {
        if (isDragging) return
        const url = urls[code]
        if (url) {
          const frame = document.querySelector("turbo-frame#country_modal")
          if (frame) {
            frame.src = url
          } else {
            Turbo.visit(url)
          }
        }
      },
      onRegionTooltipShow(event, tooltip, code) {
        const data = stats[code]
        if (!data) return

        const fmt = (n) => n ? n.toLocaleString() : "—"
        const fmtB = (n) => {
          if (!n) return "—"
          if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`
          if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
          if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`
          return `$${fmt(n)}`
        }
        const fmtPop = (n) => {
          if (!n) return "—"
          if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`
          if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
          if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`
          return fmt(n)
        }

        let html = `<h5 style="margin:0 0 6px;font-size:14px;font-weight:600;">${tooltip.text()}</h5>`

        if (data.company) {
          html += `<p style="margin:0 0 4px;font-size:12px;opacity:0.85;">${data.company}</p>`
        }

        html += `<div style="display:flex;gap:14px;margin-top:6px;font-size:12px;">`

        if (data.population) {
          html += `<div><div style="opacity:0.7;">Население</div><div style="font-size:15px;font-weight:700;">${fmtPop(data.population)}</div></div>`
        }
        if (data.gdp) {
          html += `<div><div style="opacity:0.7;">ВВП</div><div style="font-size:15px;font-weight:700;">${fmtB(data.gdp)}</div></div>`
        }
        if (data.accounts !== undefined) {
          html += `<div><div style="opacity:0.7;">Аккаунты</div><div style="font-size:15px;font-weight:700;">${fmt(data.accounts)}</div></div>`
        }
        if (data.revenue !== undefined) {
          html += `<div><div style="opacity:0.7;">Доход</div><div style="font-size:15px;font-weight:700;">${fmtB(data.revenue)}</div></div>`
        }

        html += `</div>`

        tooltip.text(html, true)

        const el = tooltip.getElement()
        el.style.backgroundColor = "#1a202c"
        el.style.color = "#fff"
        el.style.borderRadius = "8px"
        el.style.padding = "10px 14px"
        el.style.fontFamily = "Inter, sans-serif"
        el.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"
      }
    })
  }
}
